import { useState, useEffect, useRef, useCallback, ReactNode, CSSProperties } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import SEO from "./components/seo/SEO";
import { Section, Fade, Heading, Label, Text, Button, C } from "./components/ui";
import { NAV, CASES, TESTIMONIALS } from "./data";
import CaseStudies from "./pages/CaseStudies";
import About from "./pages/About";
import Portfolio from "./pages/Portfolio";
import Pricing from "./pages/Pricing";
import Services from "./pages/Services";
import { submitContactForm } from "./utils/api";

/* ═══════════════════════════════════════════════════════
   MINIMAL ELEGANT DESIGN TOKENS
   Primary:  Navy  #0A1929
   Accent:   Muted Coral #D4A574
   Backgrounds: White & subtle off-white
   Fonts:    Inter (clean, modern sans-serif)
═══════════════════════════════════════════════════════ */
// Design tokens (C) now imported from ./components/ui

/* ═══ DATA (from PPT + DOCX + Brochure) ═══════════════ */
// Data constants now imported from ./data
// const NAV, CASES, TESTIMONIALS are imported from ./data

const PILLARS = [
  {
    n: "01",
    icon: "◇",
    title: "Strategy & Growth",
    role: "The foundation",
    desc: "Market analysis, customer journey mapping, and strategic planning that drives real revenue — no guesswork.",
    inc: [
      "Brand Strategy",
      "Go-To-Market Strategy",
      "Funnel & Conversion Planning",
      "Customer Journey Mapping",
      "Market & Competitor Research",
      "Analytics & Growth Planning",
    ],
  },
  {
    n: "02",
    icon: "○",
    title: "Branding & Creative",
    role: "Visual identity",
    desc: "Logos, visual systems, and messaging that make local businesses look premium and memorable.",
    inc: [
      "Brand Identity (logo, visual system)",
      "Brand Guidelines",
      "Copywriting & Messaging",
      "Ad Creatives",
      "Social Media Design",
      "Visual Direction",
    ],
  },
  {
    n: "03",
    icon: "□",
    title: "Web & Development",
    role: "Digital presence",
    desc: "Websites, landing pages, and e-commerce stores built to convert visitors into customers.",
    inc: [
      "Website Design & Development",
      "Landing Pages",
      "E-commerce Development",
      "UI/UX Design",
      "Website Optimization (speed, CRO)",
    ],
  },
  {
    n: "04",
    icon: "△",
    title: "Content & Social",
    role: "Audience engagement",
    desc: "Short-form video, social media management, and content that stops the scroll.",
    inc: [
      "Short-form Video (Reels, TikTok, Shorts)",
      "Long-form Content (YouTube, blogs)",
      "Content Strategy & Planning",
      "Social Media Management",
      "Influencer / UGC Content",
    ],
  },
  {
    n: "05",
    icon: "◇",
    title: "Performance & Automation",
    role: "Revenue engine",
    desc: "Paid ads, SEO, email, CRM, and automation working together to scale your business.",
    inc: [
      "Paid Ads (Meta, Google, TikTok)",
      "SEO & Organic Growth",
      "Email Marketing",
      "CRM & Automation",
      "Retargeting & Funnels",
      "Conversion Optimization",
    ],
  },
];

const SOCIAL = [
  {
    id: "revbase",
    name: "RevBase",
    sub: "Build Your Presence",
    price: "$1,000 – $1,200",
    per: "/month",
    best: "Small businesses starting out",
    inc: [
      "Social media strategy (basic)",
      "8 posts/month (static + captions)",
      "Basic creative design",
      "Content scheduling & publishing",
      "Profile optimization",
      "Monthly performance report",
    ],
    out: "Show up online professionally and consistently.",
  },
  {
    id: "revboost",
    name: "RevBoost",
    sub: "Accelerate Your Growth",
    price: "$2,000 – $2,500",
    per: "/month",
    best: "Businesses ready to grow leads",
    hot: true,
    inc: [
      "Advanced strategy & content planning",
      "12–16 posts/month (incl. Reels)",
      "High-quality creative design",
      "Caption & hashtag optimization",
      "Community management (comments & DMs)",
      "Meta ads management (basic campaigns)",
      "Growth optimization",
      "Monthly reporting + strategy call",
    ],
    out: "Consistent, growing reach and new customers weekly.",
  },
  {
    id: "revmax",
    name: "RevMax",
    sub: "Dominate Your Market",
    price: "$3,800 – $5,000+",
    per: "/month",
    best: "Scaling brands & serious growth",
    inc: [
      "Full social strategy + funnel alignment",
      "20–30 content pieces/month (incl. video)",
      "Premium creative & video production",
      "Daily content management",
      "Full community management",
      "Advanced Meta ads (scaling + retargeting)",
      "Influencer / UGC campaigns",
      "Weekly optimization + reporting",
      "Dedicated account manager",
    ],
    out: "Become the most visible, trusted business in your category.",
  },
];

const AUTO = [
  {
    id: "revflow",
    name: "RevFlow",
    sub: "Streamline Your Operations",
    price: "$1,200 – $1,800",
    per: "/month",
    best: "Businesses starting automation",
    inc: [
      "Basic CRM setup",
      "Lead capture system",
      "1–2 automation workflows",
      "Email follow-up setup (basic)",
      "Appointment booking integration",
      "Monthly system check",
    ],
    out: "No more lost leads. Automatic follow-ups 24/7.",
  },
  {
    id: "revsync",
    name: "RevSync",
    sub: "Connect & Optimize Your Systems",
    price: "$2,500 – $3,500",
    per: "/month",
    best: "Growing businesses needing efficiency",
    hot: true,
    inc: [
      "Advanced CRM customization",
      "Multi-step automation workflows",
      "Email & SMS automation",
      "Sales pipeline setup",
      "Tool integrations (Zapier/API)",
      "Basic dashboard & reporting",
      "Monthly optimization",
    ],
    out: "Your business runs smoother. Close deals, don't chase them.",
  },
  {
    id: "revops",
    name: "RevOps",
    sub: "Automate & Scale Your Business",
    price: "$4,500 – $6,500+",
    per: "/month",
    best: "Scaling companies & high-ticket services",
    inc: [
      "Full business automation system",
      "Advanced CRM + pipeline management",
      "AI chatbot & lead qualification",
      "Full funnel automation",
      "Custom integrations",
      "Advanced analytics dashboard",
      "Ongoing optimization & support",
      "Dedicated automation specialist",
    ],
    out: "Your business operates like a machine. Hands-free growth.",
  },
];

const WEB = [
  {
    id: "revsite-core",
    name: "RevSite Core",
    sub: "Launch Your Digital Presence",
    price: "$1,500 – $2,500",
    per: "one-time",
    best: "Getting started with a professional presence",
    inc: [
      "3–5 page website",
      "Basic UI/UX design",
      "Mobile responsive",
      "Basic SEO setup",
      "Contact form integration",
    ],
    out: "A clean, credible website that builds trust.",
  },
  {
    id: "revsite-growth",
    name: "RevSite Growth",
    sub: "Built for Conversion",
    price: "$3,000 – $5,000",
    per: "one-time",
    best: "Converting visitors into customers",
    hot: true,
    inc: [
      "5–10 pages",
      "Conversion-focused design",
      "Landing pages",
      "Speed optimization",
      "On-page SEO",
      "Analytics setup",
    ],
    out: "A website that actively generates leads.",
  },
  {
    id: "revsite-pro",
    name: "RevSite Pro",
    sub: "High-Performance Web System",
    price: "$6,000 – $10,000+",
    per: "one-time",
    best: "Full custom web + funnel system",
    inc: [
      "Custom design & development",
      "Advanced UX/UI",
      "Funnel-based structure",
      "CRM & automation integrations",
      "Advanced SEO setup",
      "Performance optimization",
    ],
    out: "Your most powerful salesperson, working 24/7.",
  },
];

// CASES and TESTIMONIALS now imported from ./data
const PROCESS = [
  {
    n: "01",
    t: "We Analyze",
    d: "Free audit of your business, local market, and competition. We find exactly where the growth opportunity is.",
  },
  {
    n: "02",
    t: "We Strategize",
    d: "A custom growth plan using the pillars that matter most for your business. No templates — real strategy.",
  },
  {
    n: "03",
    t: "We Execute",
    d: "Content, ads, SEO, automation — our team handles all of it while you focus on running your business.",
  },
  {
    n: "04",
    t: "We Scale",
    d: "Monthly reporting, strategy calls, and continuous optimization. Results compound every month.",
  },
];

const INDUSTRIES = [
  {
    icon: "🍽",
    n: "Restaurants & Cafés",
    d: "Food content that makes people hungry before they walk in.",
  },
  {
    icon: "✂️",
    n: "Salons & Spas",
    d: "Transformation content that builds your brand and fills your chair.",
  },
  {
    icon: "💈",
    n: "Barbershops",
    d: "Culture, community, and craft — captured on video.",
  },
  {
    icon: "🛍",
    n: "Retail & Boutiques",
    d: "Product showcases and local brand storytelling.",
  },
  {
    icon: "🔧",
    n: "Service Businesses",
    d: "Professional content that converts leads into customers.",
  },
  {
    icon: "📍",
    n: "Local Brands",
    d: "Whatever you do — we make you impossible to ignore.",
  },
];

/* ═══ ANIMATION HOOKS ═══════════════════════════════════ */
// useInView and Fade now imported from ./components/ui and ./hooks

/* ═══ TYPOGRAPHY COMPONENTS ════════════════════════════ */
// Label, Heading, Text now imported from ./components/ui

interface HeadingProps {
  children: ReactNode;
  size?: 1 | 2 | 3;
  light?: boolean;
  style?: CSSProperties;
}

// Heading and Text components now imported from ./components/ui

/* ═══ BUTTON COMPONENTS ════════════════════════════════ */
// Button and Section components now imported from ./components/ui

interface ContainerProps {
  children: ReactNode;
  maxWidth?: string;
  className?: string;
}

function Container({ children, maxWidth = "1200px", className = "" }: ContainerProps) {
  return (
    <div
      className={className}
      style={{
        maxWidth,
        marginLeft: "auto",
        marginRight: "auto",
        paddingLeft: window.innerWidth < 768 ? "16px" : "24px",
        paddingRight: window.innerWidth < 768 ? "16px" : "24px",
        width: "auto",
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
}

/* ═══ CARD COMPONENTS ══════════════════════════════════ */

interface CardProps {
  children: ReactNode;
  className?: string;
  hover?: boolean;
}

function Card({ children, className = "", hover = true }: CardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      style={{
        background: C.white,
        border: `1px solid ${C.gray100}`,
        borderRadius: "12px",
        padding: "24px",
        transition: "all 0.3s ease",
        boxShadow: hovered
          ? "0 8px 30px rgba(0, 0, 0, 0.06)"
          : "0 1px 3px rgba(0, 0, 0, 0.04)",
        transform: hover && hovered ? "translateY(-2px)" : "translateY(0)",
        borderColor: hovered ? C.gray200 : C.gray100,
      }}
    >
      {children}
    </div>
  );
}

interface Package {
  id: string;
  name: string;
  sub: string;
  price: string;
  per: string;
  best: string;
  hot?: boolean;
  inc: string[];
  out?: string;
}

interface PricingCardProps {
  pkg: Package;
  onCTA: () => void;
}

function PricingCard({ pkg, onCTA }: PricingCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        background: pkg.hot ? C.navy : C.white,
        border: `2px solid ${pkg.hot ? C.navy : C.gray100}`,
        borderRadius: "16px",
        padding: "32px 28px",
        transition: "all 0.3s ease",
        boxShadow: hovered
          ? pkg.hot
            ? "0 12px 40px rgba(10, 25, 41, 0.15)"
            : "0 8px 30px rgba(0, 0, 0, 0.08)"
          : "0 1px 3px rgba(0, 0, 0, 0.04)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {pkg.hot && (
        <div
          style={{
            position: "absolute",
            top: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            background: C.coral,
            color: C.white,
            padding: "6px 16px",
            borderRadius: "20px",
            fontFamily: C.mono,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.05em",
          }}
        >
          POPULAR
        </div>
      )}

      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            fontFamily: C.font,
            fontSize: "1.5rem",
            fontWeight: 600,
            color: pkg.hot ? C.white : C.navy,
            marginBottom: "4px",
          }}
        >
          {pkg.name}
        </div>
        <div
          style={{
            fontFamily: C.mono,
            fontSize: "12px",
            color: pkg.hot ? C.gray300 : C.gray400,
            marginBottom: "20px",
          }}
        >
          {pkg.sub}
        </div>
        <div
          style={{
            fontFamily: C.font,
            fontSize: "2rem",
            fontWeight: 600,
            color: pkg.hot ? C.white : C.navy,
            marginBottom: "4px",
          }}
        >
          {pkg.price}
        </div>
        <div
          style={{
            fontFamily: C.mono,
            fontSize: "13px",
            color: pkg.hot ? C.gray400 : C.gray500,
          }}
        >
          {pkg.per}
        </div>
      </div>

      <div
        style={{
          padding: "16px",
          background: pkg.hot ? "rgba(255,255,255,0.05)" : C.gray50,
          borderRadius: "8px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            fontFamily: C.mono,
            fontSize: "11px",
            color: pkg.hot ? C.coral : C.gray500,
            marginBottom: "8px",
            fontWeight: 600,
          }}
        >
          BEST FOR
        </div>
        <div
          style={{
            fontFamily: C.font,
            fontSize: "0.9rem",
            color: pkg.hot ? C.gray200 : C.gray600,
            lineHeight: 1.5,
          }}
        >
          {pkg.best}
        </div>
      </div>

      <div
        style={{
          marginBottom: "24px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontFamily: C.mono,
            fontSize: "11px",
            color: pkg.hot ? C.coral : C.gray500,
            marginBottom: "12px",
            fontWeight: 600,
          }}
        >
          INCLUDES
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {pkg.inc.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <div
                style={{
                  color: pkg.hot ? C.coral : C.navy,
                  fontSize: "16px",
                  marginTop: "-2px",
                  flexShrink: 0,
                }}
              >
                •
              </div>
              <div
                style={{
                  fontFamily: C.font,
                  fontSize: "0.875rem",
                  color: pkg.hot ? C.gray300 : C.gray500,
                  lineHeight: 1.5,
                }}
              >
                {item}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={onCTA}
        style={{
          width: "100%",
          padding: "14px",
          fontFamily: C.font,
          fontSize: "14px",
          fontWeight: 500,
          borderRadius: "8px",
          border: "none",
          cursor: "pointer",
          transition: "all 0.2s ease",
          background: pkg.hot ? C.coral : C.navy,
          color: C.white,
          marginTop: "auto",
          ...(hovered && {
            transform: "scale(1.02)",
          }),
        }}
      >
        Get Started
      </button>
    </div>
  );
}

/* ═══ PRICING SECTION COMPONENT ═════════════════════════════ */

interface PricingSectionProps {
  activeTab: "social" | "automation" | "web";
  onTabChange: (tab: "social" | "automation" | "web") => void;
  onPlanSelect: (planName: string) => void;
  selectedPlan: string;
  compact?: boolean;
  showGetStarted?: boolean;
  goToContact?: (planName?: string) => void;
}

function PricingSection({ activeTab, onTabChange, onPlanSelect, selectedPlan, compact = false, showGetStarted = true, goToContact }: PricingSectionProps) {
  return (
    <div>
      {/* Pricing Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginBottom: compact ? "24px" : "48px",
          flexWrap: "wrap",
        }}
      >
        {[
          { id: "social" as const, label: "Social Media", icon: "📱" },
          { id: "automation" as const, label: "Automation", icon: "⚡" },
          { id: "web" as const, label: "Web Development", icon: "💻" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              padding: compact ? "10px 20px" : "14px 28px",
              background: activeTab === tab.id ? C.navy : "transparent",
              color: activeTab === tab.id ? C.white : C.gray600,
              border: activeTab === tab.id ? `2px solid ${C.navy}` : `2px solid transparent`,
              borderRadius: "30px",
              fontFamily: C.font,
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
          >
            <span style={{ fontSize: "18px" }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pricing Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: window.innerWidth < 768
            ? "1fr"
            : "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
          alignItems: "stretch",
        }}
      >
        {activeTab === "social" && SOCIAL.map((pkg) => (
          <Fade key={pkg.id} delay={0.05 * SOCIAL.indexOf(pkg)}>
            <PricingCard pkg={pkg} onCTA={() => showGetStarted && goToContact ? goToContact(pkg.name) : onPlanSelect(pkg.name)} />
          </Fade>
        ))}
        {activeTab === "automation" && AUTO.map((pkg) => (
          <Fade key={pkg.id} delay={0.05 * AUTO.indexOf(pkg)}>
            <PricingCard pkg={pkg} onCTA={() => showGetStarted && goToContact ? goToContact(pkg.name) : onPlanSelect(pkg.name)} />
          </Fade>
        ))}
        {activeTab === "web" && WEB.map((pkg) => (
          <Fade key={pkg.id} delay={0.05 * WEB.indexOf(pkg)}>
            <PricingCard pkg={pkg} onCTA={() => showGetStarted && goToContact ? goToContact(pkg.name) : onPlanSelect(pkg.name)} />
          </Fade>
        ))}
      </div>
    </div>
  );
}

/* ═══ HOME PAGE ════════════════════════════════════════ */

interface PageProps {
  go: (section: string) => void;
  goToContact: (planName?: string) => void;
  onVideoSelect: (videoId: string) => void;
  activePricingTab: "social" | "automation" | "web";
  setActivePricingTab: (tab: "social" | "automation" | "web") => void;
  contactForm: { name: string; email: string; phone: string; business: string; message: string; plan: string };
  setContactForm: React.Dispatch<React.SetStateAction<{ name: string; email: string; phone: string; business: string; message: string; plan: string }>>;
  handleContactSubmit: (e: React.FormEvent) => void;
  preselectedPlan: string;
}

interface ContactPageProps {
  go: (section: string) => void;
  goToContact: (planName?: string) => void;
  contactForm: { name: string; email: string; phone: string; business: string; message: string; plan: string };
  setContactForm: React.Dispatch<React.SetStateAction<{ name: string; email: string; phone: string; business: string; message: string; plan: string }>>;
  handleContactSubmit: (e: React.FormEvent) => void;
  preselectedPlan: string;
}

function Home({ go, goToContact, onVideoSelect, activePricingTab, setActivePricingTab, contactForm, setContactForm, handleContactSubmit, preselectedPlan }: PageProps) {
  const words = ["Customers.", "Revenue.", "Bookings.", "Growth."];
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2700);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <SEO
        title="Home"
        description="RevSpike helps local businesses dominate their markets with done-for-you marketing, branding, and content services. From restaurants to salons, we deliver real results."
      />
      {/* HERO SECTION */}
      <section
        id="home"
        style={{
          background: C.navy,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: window.innerWidth < 768 ? "100px 20px 60px" : "120px 24px 80px",
          position: "relative",
          overflow: "hidden",
          width: "100%",
          margin: 0,
          boxSizing: "border-box",
        }}
      >
        {/* Subtle grid pattern */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.03,
            backgroundImage: `radial-gradient(${C.white} 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
            pointerEvents: "none",
          }}
        />

        {/* Decorative circle */}
        <div
          style={{
            position: "absolute",
            right: "-100px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            border: "1px solid rgba(212, 165, 116, 0.1)",
            pointerEvents: "none",
            maxWidth: "50vw",
          }}
        />

        <Container maxWidth="1100px">
          <Fade>
            <Label light>RevSpike Communications — Canada</Label>
          </Fade>

          <Fade delay={0.1}>
            <div style={{ marginBottom: "24px" }}>
              <Heading size={1} light style={{ marginBottom: "16px" }}>
                More{" "}
                <span style={{ color: C.coral }}>
                  {words[wordIndex]}
                </span>
                <br />
                Not Just More Content.
              </Heading>
            </div>
          </Fade>

          <Fade delay={0.2}>
            <Text
              light
              size="lg"
              style={{ maxWidth: "540px", marginBottom: "40px" }}
            >
              RevSpike is a 360° digital growth agency helping local businesses
              across Canada get more customers — through short-form video,
              branding, web development, local SEO, and smart marketing
              automation.
            </Text>
          </Fade>

          <Fade delay={0.3}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button onClick={() => go("portfolio")} variant="coral" size={window.innerWidth < 768 ? "sm" : "default"}>
                See Our Work
              </Button>
              <Button onClick={() => goToContact()} variant="outline" size={window.innerWidth < 768 ? "sm" : "default"}>
                Get Free Growth Audit →
              </Button>
            </div>
          </Fade>

          <Fade delay={0.5}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "repeat(3, 1fr)",
                gap: "1px",
                marginTop: window.innerWidth < 768 ? "48px" : "80px",
                background: C.gray200,
                border: `1px solid ${C.gray200}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {[
                ["3×", "Avg Revenue Growth"],
                ["60+", "Local Businesses Served"],
                ["5", "Pillars · One System"],
              ].map(([stat, label], i) => (
                <div
                  key={label}
                  style={{
                    padding: "28px 20px",
                    textAlign: "center",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: C.font,
                      fontSize: "2.25rem",
                      fontWeight: 600,
                      color: C.coral,
                      marginBottom: "4px",
                    }}
                  >
                    {stat}
                  </div>
                  <div
                    style={{
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.gray400,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </Container>
      </section>

      {/* 5 PILLARS */}
      <Section id="services" background={C.gray50}>
        <Fade>
          <Label>What We Do</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} style={{ marginBottom: "12px" }}>
            5 Pillars. One Complete Growth System.
          </Heading>
        </Fade>
        <Fade delay={0.12}>
          <Text size="lg" style={{ maxWidth: "540px", marginBottom: "48px" }}>
            Most agencies hand you deliverables. We build you a machine. Five
            integrated pillars that compound over time.
          </Text>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth < 768
              ? "repeat(auto-fit, minmax(280px, 1fr))"
              : "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
          }}
        >
          {PILLARS.map((pillar, i) => (
            <Fade key={pillar.title} delay={i * 0.06}>
              <Card>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: C.gray50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    marginBottom: "16px",
                  }}
                >
                  {pillar.icon}
                </div>
                <div
                  style={{
                    fontFamily: C.mono,
                    fontSize: "11px",
                    color: C.gray400,
                    marginBottom: "8px",
                    fontWeight: 600,
                  }}
                >
                  {pillar.n}
                </div>
                <Heading
                  size={3}
                  style={{
                    marginBottom: "8px",
                    fontSize: "1.125rem",
                  }}
                >
                  {pillar.title}
                </Heading>
                <div
                  style={{
                    width: "24px",
                    height: "2px",
                    background: C.navy,
                    marginBottom: "12px",
                  }}
                />
                <Text size="sm">{pillar.desc}</Text>
              </Card>
            </Fade>
          ))}
        </div>

        <Fade delay={0.4}>
          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <Button onClick={() => go("services")} variant="ghost">
              Explore All 5 Pillars →
            </Button>
          </div>
        </Fade>
      </Section>

      {/* PORTFOLIO PREVIEW */}
      <Section id="portfolio" background={C.navy}>
        <Fade>
          <Label light>Our Work</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} light style={{ marginBottom: "12px" }}>
            Real Work. Real Results.
          </Heading>
        </Fade>
        <Fade delay={0.12}>
          <Text
            light
            size="lg"
            style={{ maxWidth: "480px", marginBottom: "48px" }}
          >
            From restaurants to barbershops — content and systems that actually
            convert.
          </Text>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth < 768
              ? "1fr"
              : "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "24px",
            alignItems: "stretch",
          }}
        >
          {[
            {
              icon: "🍽",
              category: "Restaurant",
              title: "Italian Kitchen - Reel Transformation",
              result: "3× Revenue in 90 Days",
              videoId: "dQw4w9WgXcQ",
              color: "from-orange-500/20 to-red-500/20",
            },
            {
              icon: "✂️",
              category: "Salon",
              title: "Luxe Salon - Before/After Series",
              result: "1,800 New Followers",
              videoId: "jNQXAC9IVRw",
              color: "from-pink-500/20 to-purple-500/20",
            },
            {
              icon: "💈",
              category: "Barbershop",
              title: "Classic Cuts - Culture Content",
              result: "#1 on Google Maps",
              videoId: "9bZkp7q19f0",
              color: "from-blue-500/20 to-cyan-500/20",
            },
          ].map((item, index) => (
            <Fade key={item.title} delay={index * 0.1}>
              <div
                onClick={() => onVideoSelect(item.videoId)}
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(212, 165, 116, 0.5)";
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(212, 165, 116, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Video Thumbnail */}
                <div
                  style={{
                    position: "relative",
                    aspectRatio: "16/9",
                    background: `linear-gradient(135deg, rgba(10, 25, 41, 0.8), rgba(26, 41, 56, 0.9))`,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`}
                    alt={item.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      opacity: 0.85,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(10, 25, 41, 0.9), transparent)",
                    }}
                  />

                  {/* Play Button */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "rgba(212, 165, 116, 0.95)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 24px rgba(212, 165, 116, 0.4)",
                      border: "3px solid rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="white"
                      style={{ marginLeft: "3px" }}
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>

                  {/* Category Badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: "16px",
                      left: "16px",
                      background: "rgba(10, 25, 41, 0.9)",
                      backdropFilter: "blur(8px)",
                      padding: "8px 16px",
                      borderRadius: "20px",
                      border: "1px solid rgba(212, 165, 116, 0.3)",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: C.coral,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {item.category.toUpperCase()}
                  </div>

                  {/* Icon */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "16px",
                      right: "16px",
                      fontSize: "2rem",
                      opacity: 0.9,
                    }}
                  >
                    {item.icon}
                  </div>
                </div>

                {/* Card Content */}
                <div
                  style={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      fontFamily: C.font,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: C.white,
                      marginBottom: "12px",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.title}
                  </div>

                  {/* Result Badge */}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "rgba(39, 174, 96, 0.15)",
                      border: "1px solid rgba(39, 174, 96, 0.3)",
                      padding: "8px 14px",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#27AE60",
                        boxShadow: "0 0 12px rgba(39, 174, 96, 0.6)",
                      }}
                    />
                    <span
                      style={{
                        fontFamily: C.mono,
                        fontSize: "11px",
                        color: "#27AE60",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      {item.result}
                    </span>
                  </div>
                </div>
              </div>
            </Fade>
          ))}
        </div>

        <Fade delay={0.4}>
          <div style={{ textAlign: "center", marginTop: "48px" }}>
            <Button onClick={() => go("portfolio")} variant="coral" size="lg">
              Watch All Case Studies →
            </Button>
          </div>
        </Fade>
      </Section>

      {/* PROCESS */}
      <Section background={C.white}>
        <Fade>
          <Label>The Process</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} style={{ marginBottom: "48px" }}>
            Simple. Proven. Built for Local.
          </Heading>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "32px",
          }}
        >
          {PROCESS.map((step, i) => (
            <Fade key={step.n} delay={i * 0.1}>
              <div>
                <div
                  style={{
                    fontFamily: C.font,
                    fontSize: "3rem",
                    fontWeight: 600,
                    color: C.gray100,
                    lineHeight: 1,
                    marginBottom: "-8px",
                  }}
                >
                  {step.n}
                </div>
                <Heading
                  size={3}
                  style={{
                    marginBottom: "12px",
                    fontSize: "1.125rem",
                  }}
                >
                  {step.t}
                </Heading>
                <div
                  style={{
                    width: "24px",
                    height: "2px",
                    background: C.navy,
                    marginBottom: "12px",
                  }}
                />
                <Text size="sm">{step.d}</Text>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* INDUSTRIES */}
      <Section id="about" background={C.gray50}>
        <Fade>
          <Label>Industries We Serve</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} style={{ marginBottom: "48px" }}>
            We Know Your Business.
          </Heading>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          {INDUSTRIES.map((ind, i) => (
            <Fade key={ind.n} delay={i * 0.08}>
              <Card>
                <div style={{ fontSize: "1.5rem", marginBottom: "12px" }}>
                  {ind.icon}
                </div>
                <Heading
                  size={3}
                  style={{
                    marginBottom: "8px",
                    fontSize: "1rem",
                  }}
                >
                  {ind.n}
                </Heading>
                <Text size="sm">{ind.d}</Text>
              </Card>
            </Fade>
          ))}
        </div>
      </Section>

      {/* PRICING PREVIEW */}
      <Section id="pricing" background={C.white}>
        <Fade>
          <Label>Pricing</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} style={{ marginBottom: "8px" }}>
            Transparent Pricing. No Surprises.
          </Heading>
        </Fade>
        <Fade delay={0.12}>
          <Text size="base" style={{ marginBottom: "8px" }}>
            Choose the package that fits your business. All prices in CAD.
          </Text>
        </Fade>
        <Fade delay={0.16}>
          <div
            style={{
              fontFamily: C.mono,
              fontSize: "11px",
              color: C.gray400,
              letterSpacing: "0.1em",
              marginBottom: "40px",
              fontWeight: 600,
            }}
          >
            MONTH-TO-MONTH · NO LONG-TERM CONTRACTS
          </div>
        </Fade>

        <Fade delay={0.2}>
          <PricingSection
            activeTab={activePricingTab}
            onTabChange={setActivePricingTab}
            onPlanSelect={(planName) => setContactForm(prev => ({ ...prev, plan: planName }))}
            selectedPlan={contactForm.plan}
            showGetStarted={true}
            goToContact={goToContact}
          />
        </Fade>
      </Section>

      {/* TESTIMONIALS */}
      <Section id="cases" background={C.navy}>
        <Fade>
          <Label light>What Clients Say</Label>
        </Fade>
        <Fade delay={0.08}>
          <Heading size={2} light style={{ marginBottom: "48px" }}>
            Local Businesses Trust RevSpike.
          </Heading>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: window.innerWidth < 768
              ? "repeat(auto-fit, minmax(280px, 1fr))"
              : "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          {TESTIMONIALS.map((t, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid " + "rgba(255,255,255,0.06)",
                  borderRadius: "12px",
                  padding: "28px 24px",
                }}
              >
                <div
                  style={{
                    fontFamily: C.font,
                    fontSize: "2.5rem",
                    color: C.coral,
                    lineHeight: 0.8,
                    marginBottom: "16px",
                  }}
                >
                  "
                </div>
                <Text
                  light
                  size="base"
                  style={{
                    marginBottom: "20px",
                    fontStyle: "italic",
                  }}
                >
                  {t.q}
                </Text>
                <div
                  style={{
                    fontFamily: C.mono,
                    fontSize: "12px",
                    color: C.coral,
                    marginBottom: "4px",
                    fontWeight: 600,
                  }}
                >
                  {t.n}
                </div>
                <div
                  style={{
                    fontFamily: C.mono,
                    fontSize: "11px",
                    color: C.gray400,
                    letterSpacing: "0.05em",
                  }}
                >
                  {t.r}
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* FINAL CTA - CONTACT FORM */}
      <section
        id="contact"
        style={{
          background: C.navy,
          padding: "100px 24px",
          width: "100%",
          margin: 0,
          boxSizing: "border-box",
        }}
      >
        <Container maxWidth="700px">
          <Fade>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <Label light>Get Your Free Growth Audit</Label>
              <Heading size={2} light style={{ marginBottom: "16px" }}>
                Ready to Get More Customers?
              </Heading>
              <Text
                light
                size="lg"
                style={{ maxWidth: "540px", margin: "0 auto" }}
              >
                No pressure. No sales pitch. Let's look at your business, your
                market, and what's holding you back — for free.
              </Text>
            </div>
          </Fade>

          <Fade delay={0.1}>
            <form
              onSubmit={handleContactSubmit}
              style={{
                padding: "0",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: window.innerWidth < 600 ? "1fr" : "repeat(2, 1fr)",
                  gap: "20px",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.coral,
                      marginBottom: "8px",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                    }}
                  >
                    YOUR NAME *
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    placeholder="John Smith"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "none",
                      borderRadius: "8px",
                      color: C.white,
                      fontFamily: C.font,
                      fontSize: "15px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.coral,
                      marginBottom: "8px",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                    }}
                  >
                    EMAIL ADDRESS *
                  </label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    placeholder="john@company.com"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "none",
                      borderRadius: "8px",
                      color: C.white,
                      fontFamily: C.font,
                      fontSize: "15px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.coral,
                      marginBottom: "8px",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                    }}
                  >
                    PHONE NUMBER
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    placeholder="(416) 555-0199"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "none",
                      borderRadius: "8px",
                      color: C.white,
                      fontFamily: C.font,
                      fontSize: "15px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.coral,
                      marginBottom: "8px",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                    }}
                  >
                    BUSINESS NAME
                  </label>
                  <input
                    type="text"
                    value={contactForm.business}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, business: e.target.value })
                    }
                    placeholder="Your Business Name"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "none",
                      borderRadius: "8px",
                      color: C.white,
                      fontFamily: C.font,
                      fontSize: "15px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontFamily: C.mono,
                    fontSize: "11px",
                    color: C.coral,
                    marginBottom: "8px",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                  }}
                >
                  TELL US ABOUT YOUR BUSINESS *
                </label>
                <textarea
                  required
                  value={contactForm.message}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, message: e.target.value })
                  }
                  placeholder="What services are you interested in? What are your current challenges?"
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "none",
                    borderRadius: "8px",
                    color: C.white,
                    fontFamily: C.font,
                    fontSize: "15px",
                    boxSizing: "border-box",
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                />
              </div>

              <div style={{ textAlign: "center" }}>
                <Button
                  type="submit"
                  variant="coral"
                  size="lg"
                  style={{ width: window.innerWidth < 600 ? "100%" : "auto" }}
                >
                  Request Free Growth Audit →
                </Button>
                <div
                  style={{
                    fontFamily: C.mono,
                    fontSize: "10px",
                    color: C.gray400,
                    marginTop: "16px",
                    letterSpacing: "0.1em",
                  }}
                >
                  We'll respond within 24 hours
                </div>
              </div>
            </form>
          </Fade>
        </Container>
      </section>
    </>
  );
}

/* ═══ CONTACT PAGE ═════════════════════════════════════ */

function Contact({ go, goToContact, contactForm, setContactForm, handleContactSubmit, preselectedPlan }: ContactPageProps) {
  const [activePricingTab, setActivePricingTab] = useState<"social" | "automation" | "web">("social");

  useEffect(() => {
    if (preselectedPlan && !contactForm.plan) {
      setContactForm(prev => ({ ...prev, plan: preselectedPlan }));
    }
  }, [preselectedPlan]);

  return (
    <>
      {/* HERO SECTION */}
      <Section background={C.navy} padding="140px 28px 80px">
        <Container maxWidth="900px">
          <Fade>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <Label light>Get Your Free Growth Audit</Label>
              <Heading size={1} light style={{ marginBottom: "20px" }}>
                Book a Free 30-Min Strategy Call
              </Heading>
              <Text
                light
                size="lg"
                style={{ maxWidth: "600px", margin: "0 auto" }}
              >
                We'll review your current presence, your local market, and your goals — then tell you exactly what we'd do and which packages make sense for your business.
              </Text>
            </div>
          </Fade>

          {/* FEATURE CARDS */}
          <Fade delay={0.15}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: window.innerWidth < 768
                  ? "1fr"
                  : "repeat(3, 1fr)",
                gap: "24px",
                marginBottom: "60px",
              }}
            >
              {[
                {
                  icon: "📍",
                  title: "Serving Canadian Businesses",
                  desc: "We know the Canadian market and what works for local businesses coast to coast."
                },
                {
                  icon: "⚡",
                  title: "Response Within 24 Hours",
                  desc: "Every inquiry gets a personal response within one business day."
                },
                {
                  icon: "🎯",
                  title: "Free Audit Included",
                  desc: "Every intro call includes a real audit — no strings attached."
                }
              ].map((feature, i) => (
                <div
                  key={i}
                  style={{
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: "16px",
                    padding: "28px 24px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "2.5rem", marginBottom: "16px" }}>
                    {feature.icon}
                  </div>
                  <Heading
                    size={3}
                    light
                    style={{ marginBottom: "12px", fontSize: "1.125rem" }}
                  >
                    {feature.title}
                  </Heading>
                  <Text
                    light
                    size="sm"
                    style={{ color: C.gray300, lineHeight: 1.6 }}
                  >
                    {feature.desc}
                  </Text>
                </div>
              ))}
            </div>
          </Fade>
        </Container>
      </Section>

      {/* PRICING REFERENCE & FORM */}
      <Section background={C.gray50} padding="80px 28px">
        <Container maxWidth="1100px">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: window.innerWidth < 900 ? "1fr" : "1fr 1.2fr",
              gap: "60px",
              alignItems: "start",
            }}
          >
            {/* PRICING SELECTION */}
            <Fade delay={0.2}>
              <div>
                <Label>Pricing Plans</Label>
                <Heading size={2} style={{ marginBottom: "32px" }}>
                  Choose Your Package
                </Heading>

                <div style={{ marginBottom: "32px" }}>
                  <label
                    style={{
                      display: "block",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.gray400,
                      marginBottom: "16px",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                    }}
                  >
                    SELECT A PRICING PLAN (OPTIONAL)
                  </label>

                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {[
                      { id: "social", name: "Social Media Marketing", plans: ["RevBase", "RevBoost", "RevMax"] },
                      { id: "automation", name: "Marketing Automation", plans: ["RevFlow", "RevSync", "RevOps"] },
                      { id: "web", name: "Web Development", plans: ["RevSite Core", "RevSite Growth", "RevSite Pro"] }
                    ].map((category) => (
                      <div key={category.id}>
                        <Text
                          size="sm"
                          style={{
                            color: C.gray600,
                            fontWeight: 600,
                            marginBottom: "8px",
                            fontSize: "13px",
                          }}
                        >
                          {category.name}
                        </Text>
                        {category.plans.map((plan) => (
                          <label
                            key={plan}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "12px 16px",
                              background: contactForm.plan === plan ? "rgba(212, 165, 116, 0.1)" : C.white,
                              border: `1px solid ${contactForm.plan === plan ? C.coral : C.gray200}`,
                              borderRadius: "8px",
                              marginBottom: "8px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (contactForm.plan !== plan) {
                                e.currentTarget.style.background = C.gray50;
                                e.currentTarget.style.borderColor = C.gray300;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (contactForm.plan !== plan) {
                                e.currentTarget.style.background = C.white;
                                e.currentTarget.style.borderColor = C.gray200;
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="pricing-plan"
                              value={plan}
                              checked={contactForm.plan === plan}
                              onChange={(e) =>
                                setContactForm(prev => ({ ...prev, plan: e.target.value }))
                              }
                              style={{
                                marginRight: "12px",
                                cursor: "pointer",
                                accentColor: C.coral,
                              }}
                            />
                            <Text size="sm" style={{ margin: 0, color: C.gray600 }}>
                              {plan}
                            </Text>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    padding: "20px",
                    background: C.navy,
                    borderRadius: "12px",
                  }}
                >
                  <Text
                    size="sm"
                    style={{ color: C.white, lineHeight: 1.6 }}
                  >
                    Not sure which plan is right for you? No problem — just fill out the form and we'll recommend the best fit during your free strategy call.
                  </Text>
                </div>
              </div>
            </Fade>

            {/* CONTACT FORM */}
            <Fade delay={0.3}>
              <div
                style={{
                  background: C.white,
                  borderRadius: "20px",
                  padding: window.innerWidth < 600 ? "32px 24px" : "40px",
                  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
                }}
              >
                <Heading size={3} style={{ marginBottom: "8px" }}>
                  Get Started
                </Heading>
                <Text
                  size="sm"
                  style={{ marginBottom: "28px", color: C.gray500 }}
                >
                  Fill out the form below and we'll be in touch within 24 hours.
                </Text>

                <form
                  onSubmit={handleContactSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: window.innerWidth < 600 ? "1fr" : "repeat(2, 1fr)",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontFamily: C.mono,
                          fontSize: "11px",
                          color: C.gray400,
                          marginBottom: "8px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                        }}
                      >
                        YOUR NAME *
                      </label>
                      <input
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, name: e.target.value })
                        }
                        placeholder="John Smith"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          background: C.white,
                          border: "none",
                          borderRadius: "8px",
                          color: C.gray600,
                          fontFamily: C.font,
                          fontSize: "15px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontFamily: C.mono,
                          fontSize: "11px",
                          color: C.gray400,
                          marginBottom: "8px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                        }}
                      >
                        EMAIL ADDRESS *
                      </label>
                      <input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, email: e.target.value })
                        }
                        placeholder="john@company.com"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          background: C.white,
                          border: "none",
                          borderRadius: "8px",
                          color: C.gray600,
                          fontFamily: C.font,
                          fontSize: "15px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontFamily: C.mono,
                          fontSize: "11px",
                          color: C.gray400,
                          marginBottom: "8px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                        }}
                      >
                        PHONE NUMBER
                      </label>
                      <input
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, phone: e.target.value })
                        }
                        placeholder="(416) 555-0199"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          background: C.white,
                          border: "none",
                          borderRadius: "8px",
                          color: C.gray600,
                          fontFamily: C.font,
                          fontSize: "15px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontFamily: C.mono,
                          fontSize: "11px",
                          color: C.gray400,
                          marginBottom: "8px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                        }}
                      >
                        BUSINESS NAME
                      </label>
                      <input
                        type="text"
                        value={contactForm.business}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, business: e.target.value })
                        }
                        placeholder="Your Business Name"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          background: C.white,
                          border: "none",
                          borderRadius: "8px",
                          color: C.gray600,
                          fontFamily: C.font,
                          fontSize: "15px",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontFamily: C.mono,
                        fontSize: "11px",
                        color: C.gray400,
                        marginBottom: "8px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                      }}
                    >
                      INTERESTED PLAN
                    </label>
                    <input
                      type="text"
                      value={contactForm.plan}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, plan: e.target.value })
                      }
                      placeholder="Which pricing plan are you interested in?"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        background: C.white,
                        border: "none",
                        borderRadius: "8px",
                        color: C.gray600,
                        fontFamily: C.font,
                        fontSize: "15px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontFamily: C.mono,
                        fontSize: "11px",
                        color: C.gray400,
                        marginBottom: "8px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                      }}
                    >
                      TELL US ABOUT YOUR BUSINESS *
                    </label>
                    <textarea
                      required
                      value={contactForm.message}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, message: e.target.value })
                      }
                      placeholder="What services are you interested in? What are your current challenges?"
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        background: C.white,
                        border: "none",
                        borderRadius: "8px",
                        color: C.gray600,
                        fontFamily: C.font,
                        fontSize: "15px",
                        boxSizing: "border-box",
                        resize: "vertical",
                        minHeight: "120px",
                      }}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="coral"
                    size="lg"
                    style={{ width: window.innerWidth < 600 ? "100%" : "100%" }}
                  >
                    Book Your Free Strategy Call →
                  </Button>

                  <div
                    style={{
                      fontFamily: C.mono,
                      fontSize: "10px",
                      color: C.gray400,
                      textAlign: "center",
                      letterSpacing: "0.1em",
                    }}
                  >
                    We'll respond within 24 hours • No commitment required
                  </div>
                </form>
              </div>
            </Fade>
          </div>
        </Container>
      </Section>
    </>
  );
}

/* ═══ MAIN APP COMPONENT ═══════════════════════════════ */


function InnerApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [activePricingTab, setActivePricingTab] = useState<"social" | "automation" | "web">("social");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    message: "",
    plan: "",
  });
  const [preselectedPlan, setPreselectedPlan] = useState<string>("");

  // Reset body and html styles to remove default margins
  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.documentElement.style.margin = "0";
    document.documentElement.style.padding = "0";
    document.documentElement.style.boxSizing = "border-box";
    document.body.style.boxSizing = "border-box";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
  }, []);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedVideo) {
        setSelectedVideo(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedVideo]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedVideo]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Show loading toast
      const loadingToast = toast.loading("Sending your message...", {
        position: "top-right",
      });

      // Submit form
      await submitContactForm(
        contactForm,
        location.pathname === "/contact" ? "contact_page" : "other_page"
      );

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success("Thank you for your interest! We'll get back to you within 24 hours.", {
        position: "top-right",
        autoClose: 5000,
      });

      // Reset form
      setContactForm({
        name: "",
        email: "",
        phone: "",
        business: "",
        message: "",
        plan: "",
      });
      setPreselectedPlan("");
    } catch (error) {
      // Show error toast
      toast.error(
        error instanceof Error
          ? error.message
          : "There was an error submitting your message. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const go = (sectionId: string) => {
    const pages = ["home", "services", "contact"];
    const homeSections = ["portfolio", "pricing", "cases", "about"];

    if (sectionId === "home") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (pages.includes(sectionId)) {
      navigate(`/${sectionId === "home" ? "" : sectionId}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (homeSections.includes(sectionId)) {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  const goToContact = (planName?: string) => {
    navigate("/contact");
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (planName) {
      setPreselectedPlan(planName);
      setContactForm(prev => ({ ...prev, plan: planName }));
    }
  };

  return (
    <div
      style={{
        fontFamily: C.font,
        WebkitFontSmoothing: "antialiased",
        margin: 0,
        padding: 0,
        width: "100%",
        overflowX: "hidden",
      }}
    >
      {/* NAVIGATION */}
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${C.gray100}`,
          margin: 0,
          padding: 0,
        }}
      >
        <Container>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "64px",
            }}
          >
            <Link
              to="/"
              style={{
                fontFamily: C.font,
                fontSize: "20px",
                fontWeight: 600,
                color: C.navy,
                textDecoration: "none",
                letterSpacing: "-0.02em",
              }}
            >
              RevSpike
            </Link>

            {/* Desktop Navigation */}
            <nav
              style={{
                display: window.innerWidth >= 768 ? "flex" : "none",
                gap: "32px",
                alignItems: "center",
              }}
            >
              {NAV.map((item) => (
                <Link
                  key={item.p}
                  to={item.p === "home" ? "/" : `/${item.p}`}
                  style={{
                    fontFamily: C.font,
                    fontSize: "14px",
                    fontWeight: 500,
                    color: location.pathname === (item.p === "home" ? "/" : `/${item.p}`) ? C.navy : C.gray600,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    transition: "color 0.2s",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = C.navy;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = C.gray600;
                  }}
                >
                  {item.l}
                </Link>
              ))}
            </nav>

            <Button
              onClick={() => goToContact()}
              variant="primary"
              size="sm"
              style={{ display: window.innerWidth >= 768 ? "inline-block" : "none" }}
            >
              Get Started
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                display: window.innerWidth < 768 ? "block" : "none",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.navy}
                strokeWidth="2"
              >
                {mobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <>
                    <path d="M3 12h18M3 6h18M3 18h18" />
                  </>
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              style={{
                position: "absolute",
                top: "64px",
                left: 0,
                right: 0,
                background: C.white,
                borderBottom: `1px solid ${C.gray100}`,
                padding: "20px 0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {NAV.map((item) => (
                  <Link
                    key={item.p}
                    to={item.p === "home" ? "/" : `/${item.p}`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      fontFamily: C.font,
                      fontSize: "16px",
                      fontWeight: 500,
                      color: C.navy,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      padding: "8px 0",
                      textDecoration: "none",
                    }}
                  >
                    {item.l}
                  </Link>
                ))}
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    goToContact();
                  }}
                  variant="primary"
                  style={{ width: "100%", marginTop: "8px" }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </Container>
      </header>

      <main
        style={{
          paddingTop: "64px",
          margin: 0,
          padding: 0,
          width: "100%",
        }}
      >
        <Routes>
          <Route path="/" element={
            <Home
              go={go}
              goToContact={goToContact}
              onVideoSelect={setSelectedVideo}
              activePricingTab={activePricingTab}
              setActivePricingTab={setActivePricingTab}
              contactForm={contactForm}
              setContactForm={setContactForm}
              handleContactSubmit={handleContactSubmit}
              preselectedPlan={preselectedPlan}
            />
          } />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={
            <Contact
              go={go}
              goToContact={goToContact}
              contactForm={contactForm}
              setContactForm={setContactForm}
              handleContactSubmit={handleContactSubmit}
              preselectedPlan={preselectedPlan}
            />
          } />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/cases" element={<CaseStudies />} />
          <Route path="/about" element={<About />} />
        </Routes>
      </main>

      {/* FOOTER */}
      <footer
        style={{
          background: C.navy,
          color: C.white,
          padding: "60px 0 30px",
          margin: 0,
          width: "100%",
        }}
      >
        <Container maxWidth="1100px">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "40px",
              marginBottom: "40px",
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: C.font,
                  fontSize: "20px",
                  fontWeight: 600,
                  marginBottom: "16px",
                }}
              >
                RevSpike
              </div>
              <div
                style={{
                  fontFamily: C.font,
                  fontSize: "14px",
                  color: C.gray400,
                  lineHeight: 1.6,
                }}
              >
                360° digital growth agency helping local businesses across Canada get more customers.
              </div>
            </div>

            <div>
              <div
                style={{
                  fontFamily: C.mono,
                  fontSize: "11px",
                  color: C.coral,
                  marginBottom: "16px",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                }}
              >
                QUICK LINKS
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {NAV.map((item) => (
                  <Link
                    key={item.p}
                    to={item.p === "home" ? "/" : `/${item.p}`}
                    style={{
                      fontFamily: C.font,
                      fontSize: "14px",
                      color: C.gray300,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      padding: 0,
                      textDecoration: "none",
                    }}
                  >
                    {item.l}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontFamily: C.mono,
                  fontSize: "11px",
                  color: C.coral,
                  marginBottom: "16px",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                }}
              >
                CONTACT
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    fontFamily: C.font,
                    fontSize: "14px",
                    color: C.gray300,
                  }}
                >
                  Toronto, Canada
                </div>
                <Button
                  onClick={() => goToContact()}
                  variant="outline"
                  size="sm"
                  style={{ marginTop: "8px" }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>

          <div
            style={{
              paddingTop: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: C.mono,
                fontSize: "11px",
                color: C.gray400,
                letterSpacing: "0.05em",
              }}
            >
              © 2024 RevSpike Communications. All rights reserved.
            </div>
          </div>
        </Container>
      </footer>

      {/* VIDEO MODAL */}
      {selectedVideo && (
        <div
          onClick={() => setSelectedVideo(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1000px",
              aspectRatio: "16 / 9",
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                borderRadius: "12px",
              }}
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <InnerApp />
    </BrowserRouter>
  );
}

export default App;
